/*!
 * AudoChat - Chat Bate (Modern Chat Interface)
 * تصميم حديث ومتطور للدردشة مع ريل تايم كامل
 */

import { useState, useEffect, useRef, useCallback } from "react";

// @mui material components
import {
  IconButton,
  TextField,
  Avatar,
  Chip,
  Card,
  useMediaQuery,
  useTheme,
  Grid,
  CircularProgress,
  InputAdornment,
  Badge,
} from "@mui/material";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Vision UI Dashboard React context
import { useVisionUIController } from "context";

// Vision UI Dashboard React base styles
import colors from "assets/theme/base/colors";
import linearGradient from "assets/theme/functions/linearGradient";

// React icons
import { BsChatDots, BsRobot, BsSearch, BsThreeDotsVertical } from "react-icons/bs";
import { IoMdSend, IoArrowBack } from "react-icons/io";
import { MdDone, MdDoneAll, MdSchedule, MdError } from "react-icons/md";
import { HiOutlineDotsVertical } from "react-icons/hi";

// API
import api from "utils/api";
import auth from "utils/auth";
import { supabase } from "lib/supabase";
import {
  subscribeToConversations,
  subscribeToMessages,
  subscribeToAllMessages,
} from "utils/realtime";

const {
  secondary,
  info,
  text,
  dark,
  gradients,
  borderCol,
  error,
  success,
  orange,
  primary,
} = colors;
const { card } = gradients;

// Helper function to get date components في توقيت البحرين
const getBahrainDateComponents = (date) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Bahrain",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });

  const parts = formatter.formatToParts(date);
  const year = parseInt(parts.find((p) => p.type === "year").value, 10);
  const month = parseInt(parts.find((p) => p.type === "month").value, 10) - 1;
  const day = parseInt(parts.find((p) => p.type === "day").value, 10);

  return { year, month, day };
};

// Helper function to format timestamp
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const nowComponents = getBahrainDateComponents(now);
  const msgComponents = getBahrainDateComponents(date);

  const timeString = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Bahrain",
  });

  const isToday =
    msgComponents.year === nowComponents.year &&
    msgComponents.month === nowComponents.month &&
    msgComponents.day === nowComponents.day;

  if (isToday) {
    return `Today ${timeString}`;
  }

  const todayBahrainMidnightUtc = new Date(
    Date.UTC(nowComponents.year, nowComponents.month, nowComponents.day)
  );
  const yesterdayBahrainMidnightUtc = new Date(
    todayBahrainMidnightUtc.getTime() - 24 * 60 * 60 * 1000
  );
  const yesterdayComponents = getBahrainDateComponents(yesterdayBahrainMidnightUtc);

  const isYesterday =
    msgComponents.year === yesterdayComponents.year &&
    msgComponents.month === yesterdayComponents.month &&
    msgComponents.day === yesterdayComponents.day;

  if (isYesterday) {
    return `Yesterday ${timeString}`;
  }

  const dateString = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: msgComponents.year !== nowComponents.year ? "numeric" : undefined,
    timeZone: "Asia/Bahrain",
  });

  return `${dateString} ${timeString}`;
};

// Helper function to detect Arabic
const containsArabic = (text) => {
  if (!text) return false;
  const arabicRegex = /[\u0600-\u06FF]/;
  return arabicRegex.test(text);
};

const getTextDirection = (text) => {
  return containsArabic(text) ? "rtl" : "ltr";
};

function ChatBate() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [controller] = useVisionUIController();

  // State
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [message, setMessage] = useState("");
  const [isAIMode, setIsAIMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [messageStatuses, setMessageStatuses] = useState({});
  const [messageErrors, setMessageErrors] = useState({}); // Track error messages for failed messages
  const [inputDirection, setInputDirection] = useState("ltr");

  // Refs
  const messagesEndRef = useRef(null);
  const conversationsSubscriptionRef = useRef(null);
  const messagesSubscriptionRef = useRef(null);
  const globalMessagesSubscriptionRef = useRef(null);
  const selectedConversationRef = useRef(null); // Ref to track current selected conversation

  // Pulse animation
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 0.7; }
        50% { opacity: 1; }
      }
      @keyframes slideIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getConversations();
      const transformed = (data || []).map((conv) => ({
        ...conv,
        aiMode: conv.ai_mode !== undefined ? conv.ai_mode : true,  // Default to TRUE
        messages: [],
        timestamp: conv.updated_at || conv.created_at || new Date().toISOString(),
        formattedTimestamp: formatTimestamp(
          conv.updated_at || conv.created_at || new Date().toISOString()
        ),
      }));

      transformed.sort((a, b) => {
        const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
        const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
        return timeB - timeA;
      });

      setConversations(transformed);
      setFilteredConversations(transformed);
    } catch (error) {
      console.error("Error loading conversations:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search filter
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredConversations(conversations);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = conversations.filter(
      (conv) =>
        conv.name?.toLowerCase().includes(query) ||
        conv.phone?.toLowerCase().includes(query) ||
        conv.last_message?.toLowerCase().includes(query)
    );
    setFilteredConversations(filtered);
  }, [searchQuery, conversations]);

  // Subscribe to conversations - runs only once on mount
  useEffect(() => {
    loadConversations();

    conversationsSubscriptionRef.current = subscribeToConversations((payload) => {
      if (payload.eventType === "INSERT") {
        const baseTimestamp =
          payload.new.updated_at || payload.new.created_at || new Date().toISOString();
        const newConv = {
          ...payload.new,
          aiMode: payload.new.ai_mode || false,
          messages: [],
          timestamp: baseTimestamp,
          formattedTimestamp: formatTimestamp(baseTimestamp),
        };

        setConversations((prev) => {
          const updated = [newConv, ...prev];
          updated.sort((a, b) => {
            const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
            const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
            return timeB - timeA;
          });
          return updated;
        });
      } else if (payload.eventType === "UPDATE") {
        const baseTimestamp =
          payload.new.updated_at || payload.new.created_at || new Date().toISOString();

        setConversations((prev) => {
          const updated = prev.map((conv) =>
            conv.id === payload.new.id
              ? {
                  ...conv,
                  ...payload.new,
                  aiMode: payload.new.ai_mode || false,
                  messages: conv.messages || [],
                  timestamp: baseTimestamp,
                  formattedTimestamp: formatTimestamp(baseTimestamp),
                }
              : conv
          );

          updated.sort((a, b) => {
            const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
            const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
            return timeB - timeA;
          });
          return updated;
        });

        // Update selected conversation if it's the one being updated
        setSelectedConversation((currentSelected) => {
          if (currentSelected?.id === payload.new.id) {
            return {
              ...currentSelected,
              ...payload.new,
              aiMode: payload.new.ai_mode || false,
            };
          }
          return currentSelected;
        });
      } else if (payload.eventType === "DELETE") {
        setConversations((prev) => prev.filter((conv) => conv.id !== payload.old.id));
        setSelectedConversation((currentSelected) => {
          if (currentSelected?.id === payload.old.id) {
            return null;
          }
          return currentSelected;
        });
      }
    });

    return () => {
      if (conversationsSubscriptionRef.current) {
        try {
          supabase.removeChannel(conversationsSubscriptionRef.current);
        } catch (error) {
          console.warn('Error cleaning up conversations subscription:', error);
        }
      }
    };
  }, [loadConversations]); // Removed selectedConversation?.id dependency

  // Update ref when selectedConversation changes
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Global messages subscription - تحديث قائمة المحادثات عند أي رسالة جديدة
  // This subscription stays active regardless of selected conversation
  useEffect(() => {
    globalMessagesSubscriptionRef.current = subscribeToAllMessages((payload) => {
      if (payload.eventType !== "INSERT") return;

      const msg = payload.new;
      if (!msg || !msg.conversation_id) return;

      const text = msg.message || msg.content || msg.text || msg.body || "";
      const msgTimestamp = msg.created_at || msg.timestamp || new Date().toISOString();

      // Use ref to access current selectedConversation without dependency
      const currentSelected = selectedConversationRef.current;

      setConversations((prev) => {
        if (!prev || prev.length === 0) return prev;

        let found = false;
        const updated = prev.map((conv) => {
          if (conv.id === msg.conversation_id) {
            found = true;
            const isOpen = currentSelected?.id === conv.id;
            const isUserMessage = msg.sender === "user";

            return {
              ...conv,
              last_message: text || conv.last_message,
              timestamp: msgTimestamp,
              updated_at: msgTimestamp,
              formattedTimestamp: formatTimestamp(msgTimestamp),
              unread: isOpen
                ? conv.unread || 0
                : isUserMessage
                ? (conv.unread || 0) + 1
                : conv.unread || 0,
            };
          }
          return conv;
        });

        if (!found) return prev;

        updated.sort((a, b) => {
          const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
          const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
          return timeB - timeA;
        });

        return updated;
      });
    });

    return () => {
      if (globalMessagesSubscriptionRef.current) {
        try {
          supabase.removeChannel(globalMessagesSubscriptionRef.current);
        } catch (error) {
          console.warn('Error cleaning up global messages subscription:', error);
        }
      }
    };
  }, []); // Empty dependency array - runs only once on mount

  // Load conversation details and subscribe to messages
  useEffect(() => {
    if (selectedConversation?.id) {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === selectedConversation.id ? { ...conv, unread: 0 } : conv
        )
      );

      loadConversationDetails(selectedConversation.id);

      if (messagesSubscriptionRef.current) {
        try {
          supabase.removeChannel(messagesSubscriptionRef.current);
        } catch (error) {
          console.warn('Error removing previous messages subscription:', error);
        }
      }

      messagesSubscriptionRef.current = subscribeToMessages(
        selectedConversation.id,
        (payload) => {
          if (payload.eventType === "INSERT") {
            let sender = payload.new.sender;
            if (!sender || sender === null || sender === undefined) {
              sender = payload.new.user_id ? "user" : "agent";
            }
            const newMessage = {
              id: payload.new.id,
              text:
                payload.new.message ||
                payload.new.content ||
                payload.new.text ||
                payload.new.body ||
                "",
              sender: sender,
              time: formatTimestamp(payload.new.created_at || payload.new.timestamp),
              created_at: payload.new.created_at || payload.new.timestamp,
              timestamp: payload.new.timestamp || payload.new.created_at,
            };

            if (newMessage.sender === "agent" || newMessage.sender === "ai") {
              setMessageStatuses((prev) => {
                if (!prev[newMessage.id]) {
                  return { ...prev, [newMessage.id]: "sent" };
                }
                return prev;
              });
            }

            setSelectedConversation((prev) => {
              if (prev && prev.id === payload.new.conversation_id) {
                const updated = {
                  ...prev,
                  messages: [...(prev.messages || []), newMessage],
                };
                setTimeout(() => {
                  if (messagesEndRef.current) {
                    messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
                  }
                }, 100);
                return updated;
              }
              return prev;
            });
          } else if (payload.eventType === "UPDATE") {
            const updatedMessage = {
              id: payload.new.id,
              text:
                payload.new.message ||
                payload.new.content ||
                payload.new.text ||
                payload.new.body ||
                "",
              sender: payload.new.sender || (payload.new.user_id ? "user" : "bot"),
              time: formatTimestamp(payload.new.created_at || payload.new.timestamp),
            };

            setSelectedConversation((prev) => {
              if (prev && prev.messages) {
                return {
                  ...prev,
                  messages: prev.messages.map((msg) =>
                    msg.id === payload.new.id ? updatedMessage : msg
                  ),
                };
              }
              return prev;
            });
          } else if (payload.eventType === "DELETE") {
            setSelectedConversation((prev) => {
              if (prev && prev.messages) {
                return {
                  ...prev,
                  messages: prev.messages.filter((msg) => msg.id !== payload.old.id),
                };
              }
              return prev;
            });
          }
        }
      );
    }

    return () => {
      if (messagesSubscriptionRef.current) {
        try {
          supabase.removeChannel(messagesSubscriptionRef.current);
        } catch (error) {
          console.warn('Error cleaning up messages subscription:', error);
        }
      }
    };
  }, [selectedConversation?.id]);

  const loadConversationDetails = async (conversationId) => {
    try {
      const conv = await api.getConversationById(conversationId);
      if (conv) {
        const { data: messages } = await supabase
          .from("messages")
          .select("*")
          .eq("conversation_id", conversationId)
          .order("created_at", { ascending: true });

        const transformedMessages = (messages || []).map((msg) => {
          let sender = msg.sender;
          if (!sender || sender === null || sender === undefined) {
            sender = msg.user_id ? "user" : "agent";
          }
          return {
            id: msg.id,
            text: msg.message || msg.content || msg.text || msg.body || "",
            sender: sender,
            time: formatTimestamp(msg.created_at || msg.timestamp),
            created_at: msg.created_at || msg.timestamp,
            timestamp: msg.timestamp || msg.created_at,
          };
        });

        const statuses = {};
        transformedMessages.forEach((msg) => {
          if ((msg.sender === "agent" || msg.sender === "ai") && msg.id) {
            statuses[msg.id] = "sent";
          }
        });
        setMessageStatuses((prev) => ({ ...prev, ...statuses }));

        const updatedConversation = {
          ...conv,
          aiMode: conv.ai_mode !== undefined ? conv.ai_mode : true,  // Default to TRUE
          messages: transformedMessages,
        };
        setSelectedConversation(updatedConversation);
        setIsAIMode(conv.ai_mode || false);

        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
          }
        }, 200);
      }
    } catch (error) {
      console.error("Error loading conversation details:", error);
    }
  };

  // Scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [selectedConversation?.messages]);

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedConversation) return;

    const messageText = message.trim();
    const sender = isAIMode ? "ai" : "agent";
    const currentUser = auth.getCurrentUser();
    const tempMessageId = `temp-${Date.now()}-${Math.random()}`;

    try {
      setMessage("");

      const messageData = await api.addMessageToConversation(selectedConversation.id, {
        message: messageText,
        text: messageText,
        content: messageText,
        body: messageText,
        sender: sender,
      });

      const messageId = messageData?.id || messageData?.[0]?.id || tempMessageId;
      setMessageStatuses((prev) => ({ ...prev, [messageId]: "sending" }));

      const whatsappPayload = {
        message: messageText,
        sender: {
          id: currentUser?.id || "unknown",
          name: currentUser?.name || "Agent",
          num: currentUser?.phone || currentUser?.email || "",
          email: currentUser?.email || "",
        },
        recipient: {
          id: selectedConversation.id?.toString() || "",
          name: selectedConversation.name || "Customer",
          num: selectedConversation.phone || "",
          email: selectedConversation.email || "",
        },
      };

      try {
        const response = await fetch("https://api.audo.chat/webhook/team-msg", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(whatsappPayload),
        });

        // Check if response is OK (status 200-299)
        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: Failed to send message`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch {
            const errorText = await response.text();
            if (errorText) errorMessage = errorText;
          }
          
          console.error("WhatsApp API error:", response.status, errorMessage);
          setMessageStatuses((prev) => ({ ...prev, [messageId]: "failed" }));
          setMessageErrors((prev) => ({ ...prev, [messageId]: errorMessage }));
          return;
        }

        const result = await response.json();

        // Handle different response formats
        // Expected response formats:
        // 1. { "status": "done" } or { "status": "success" }
        // 2. { "success": true }
        // 3. { "message": "sent" }
        // 4. HTTP 200 with empty body
        if (
          result.status === "done" || 
          result.status === "success" || 
          result.success === true ||
          result.message === "sent" ||
          (response.status >= 200 && response.status < 300 && !result.error)
        ) {
          setMessageStatuses((prev) => ({ ...prev, [messageId]: "sent" }));
          // Clear any previous error
          setMessageErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[messageId];
            return newErrors;
          });
        } else {
          const errorMessage = result.error || result.message || 'Unknown error occurred';
          console.error("WhatsApp API returned error:", result);
          setMessageStatuses((prev) => ({ ...prev, [messageId]: "failed" }));
          setMessageErrors((prev) => ({ ...prev, [messageId]: errorMessage }));
        }
      } catch (apiError) {
        const errorMessage = apiError.message || 'Network error or server unavailable';
        console.error("WhatsApp API error:", apiError);
        setMessageStatuses((prev) => ({ ...prev, [messageId]: "failed" }));
        setMessageErrors((prev) => ({ ...prev, [messageId]: errorMessage }));
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessageStatuses((prev) => ({ ...prev, [tempMessageId]: "failed" }));
      setMessageErrors((prev) => ({ ...prev, [tempMessageId]: error.message || 'Failed to send message' }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleSendMessage();
    }
  };

  const handleToggleAIMode = async () => {
    if (!selectedConversation) return;
    const newAIMode = !isAIMode;
    setIsAIMode(newAIMode);
    try {
      await api.updateConversationAIMode(selectedConversation.id, newAIMode);
      setSelectedConversation((prev) => ({
        ...prev,
        aiMode: newAIMode,
        ai_mode: newAIMode,
      }));
    } catch (error) {
      console.error("Error updating AI mode:", error);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Grid container spacing={3} sx={{ height: "calc(100vh - 200px)", minHeight: "600px" }}>
          {/* Conversations Sidebar */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background: `linear-gradient(${card.deg}, ${card.main}, ${card.state})`,
                borderRadius: "16px",
                overflow: "hidden",
                border: `1px solid ${borderCol.navbar}`,
              }}
            >
              {/* Header */}
              <VuiBox
                p={2.5}
                sx={{
                  borderBottom: `1px solid ${borderCol.navbar}`,
                  background: `linear-gradient(${card.deg}, ${card.main}, ${card.state})`,
                }}
              >
                <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <VuiTypography variant="h5" color="white" fontWeight="bold">
                    Conversations
                  </VuiTypography>
                  <IconButton
                    size="small"
                    sx={{
                      color: text.main,
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    <BsThreeDotsVertical size={18} />
                  </IconButton>
                </VuiBox>

                {/* Search */}
                <TextField
                  fullWidth
                  placeholder="Search conversations..."
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BsSearch size={16} color={text.main} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: secondary.focus,
                      color: "white",
                      borderRadius: "12px",
                      "& fieldset": {
                        borderColor: borderCol.navbar,
                      },
                      "&:hover fieldset": {
                        borderColor: info.main,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: info.main,
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "white",
                      fontSize: "14px",
                      padding: "10px 14px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: text.main,
                      opacity: 0.6,
                    },
                  }}
                />
              </VuiBox>

              {/* Conversations List */}
              <VuiBox
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: borderCol.main,
                    borderRadius: "3px",
                    "&:hover": {
                      background: info.main,
                    },
                  },
                }}
              >
                {loading ? (
                  <VuiBox p={4} display="flex" justifyContent="center">
                    <CircularProgress color="info" />
                  </VuiBox>
                ) : filteredConversations.length > 0 ? (
                  filteredConversations.map((conversation) => (
                    <VuiBox
                      key={conversation.id}
                      onClick={() => setSelectedConversation(conversation)}
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        borderBottom: `1px solid ${borderCol.navbar}`,
                        backgroundColor:
                          selectedConversation?.id === conversation.id
                            ? "rgba(0, 117, 255, 0.15)"
                            : "transparent",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                        },
                        animation: "slideIn 0.3s ease",
                      }}
                    >
                      <VuiBox display="flex" alignItems="center" gap={2}>
                        <Badge
                          badgeContent={conversation.unread || 0}
                          color="error"
                          invisible={!conversation.unread || conversation.unread === 0}
                        >
                          <Avatar
                            src={conversation.avatar}
                            sx={{
                              width: 56,
                              height: 56,
                              border: `2px solid ${info.main}`,
                              boxShadow: `0 0 0 2px ${secondary.main}`,
                            }}
                          />
                        </Badge>
                        <VuiBox sx={{ flex: 1, minWidth: 0 }}>
                          <VuiBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={0.5}
                          >
                            <VuiTypography
                              variant="button"
                              color="white"
                              fontWeight="medium"
                              sx={{
                                fontSize: "15px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {conversation.name}
                            </VuiTypography>
                            <VuiTypography
                              variant="caption"
                              color={text.main}
                              sx={{ fontSize: "11px", ml: 1, flexShrink: 0 }}
                            >
                              {conversation.formattedTimestamp ||
                                formatTimestamp(
                                  conversation.timestamp ||
                                    conversation.updated_at ||
                                    conversation.created_at
                                )}
                            </VuiTypography>
                          </VuiBox>
                          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
                            <VuiTypography
                              variant="caption"
                              color={text.main}
                              sx={{
                                fontSize: "13px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                mr: 1,
                              }}
                            >
                              {conversation.last_message || "No messages"}
                            </VuiTypography>
                            <Chip
                              icon={
                                conversation.aiMode || conversation.ai_mode ? (
                                  <BsRobot size={12} />
                                ) : (
                                  <BsChatDots size={12} />
                                )
                              }
                              label={
                                conversation.aiMode || conversation.ai_mode ? "AI" : "Manual"
                              }
                              size="small"
                              sx={{
                                backgroundColor:
                                  conversation.aiMode || conversation.ai_mode
                                    ? info.main
                                    : error.main,
                                color: "white",
                                height: "20px",
                                fontSize: "10px",
                                fontWeight: "bold",
                                "& .MuiChip-icon": {
                                  color: "white",
                                  marginLeft: "4px",
                                },
                                "& .MuiChip-label": {
                                  color: "white",
                                  paddingLeft: "4px",
                                  paddingRight: "4px",
                                },
                              }}
                            />
                          </VuiBox>
                        </VuiBox>
                      </VuiBox>
                    </VuiBox>
                  ))
                ) : (
                  <VuiBox p={4} textAlign="center">
                    <VuiTypography variant="h6" color={text.main}>
                      {searchQuery ? "No conversations found" : "No conversations yet"}
                    </VuiTypography>
                  </VuiBox>
                )}
              </VuiBox>
            </Card>
          </Grid>

          {/* Chat Window */}
          <Grid item xs={12} md={8}>
            {selectedConversation ? (
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  background: `linear-gradient(${card.deg}, ${card.main}, ${card.state})`,
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: `1px solid ${borderCol.navbar}`,
                }}
              >
                {/* Chat Header */}
                <VuiBox
                  p={2.5}
                  sx={{
                    borderBottom: `1px solid ${borderCol.navbar}`,
                    background: `linear-gradient(${card.deg}, ${card.main}, ${card.state})`,
                  }}
                >
                  <VuiBox display="flex" alignItems="center" justifyContent="space-between">
                    <VuiBox display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={selectedConversation.avatar}
                        sx={{
                          width: 48,
                          height: 48,
                          border: `2px solid ${info.main}`,
                        }}
                      />
                      <VuiBox>
                        <VuiTypography
                          variant="button"
                          color="white"
                          fontWeight="medium"
                          sx={{ fontSize: "16px" }}
                        >
                          {selectedConversation.name}
                        </VuiTypography>
                        <VuiTypography variant="caption" color={text.main} sx={{ fontSize: "12px" }}>
                          Online
                        </VuiTypography>
                      </VuiBox>
                    </VuiBox>
                    <VuiBox display="flex" alignItems="center" gap={1}>
                      <Chip
                        icon={isAIMode ? <BsRobot size={16} /> : <BsChatDots size={16} />}
                        label={isAIMode ? "AI" : "Manual"}
                        onClick={handleToggleAIMode}
                        sx={{
                          backgroundColor: isAIMode ? info.main : error.main,
                          color: "white",
                          cursor: "pointer",
                          fontSize: "13px",
                          height: "36px",
                          fontWeight: "bold",
                          border: `2px solid ${isAIMode ? info.focus : error.focus}`,
                          "&:hover": {
                            backgroundColor: isAIMode ? info.focus : error.focus,
                          },
                          "& .MuiChip-icon": {
                            color: "white",
                            marginLeft: "6px",
                          },
                          "& .MuiChip-label": {
                            color: "white",
                            fontWeight: "bold",
                            paddingLeft: "6px",
                            paddingRight: "8px",
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          color: text.main,
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                          },
                        }}
                      >
                        <HiOutlineDotsVertical />
                      </IconButton>
                    </VuiBox>
                  </VuiBox>
                </VuiBox>

                {/* Messages */}
                <VuiBox
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: 3,
                    backgroundColor: dark.body,
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230075ff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: dark.body,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: borderCol.main,
                      borderRadius: "4px",
                      "&:hover": {
                        background: info.main,
                      },
                    },
                  }}
                >
                  {selectedConversation.messages &&
                  Array.isArray(selectedConversation.messages) &&
                  selectedConversation.messages.length > 0 ? (
                    selectedConversation.messages.map((msg) => {
                      const isUser = msg.sender === "user";
                      const isAgent = msg.sender === "agent" || msg.sender === "ai";

                      return (
                        <VuiBox
                          key={msg.id || Math.random()}
                          sx={{
                            display: "flex",
                            justifyContent: isUser ? "flex-end" : "flex-start",
                            mb: 2.5,
                            animation: "slideIn 0.3s ease",
                          }}
                        >
                          <VuiBox
                            sx={{
                              maxWidth: "70%",
                              p: 2,
                              borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                              backgroundColor: isUser
                                ? success.main
                                : msg.sender === "ai"
                                ? info.main
                                : orange.main,
                              color: "white",
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                              position: "relative",
                            }}
                          >
                            {isAgent && msg.sender === "ai" && (
                              <VuiBox
                                sx={{
                                  position: "absolute",
                                  top: -8,
                                  left: 8,
                                  backgroundColor: info.main,
                                  borderRadius: "50%",
                                  width: "28px",
                                  height: "28px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: `2px solid ${dark.body}`,
                                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                                }}
                              >
                                <BsRobot size={14} color="white" />
                              </VuiBox>
                            )}
                            <VuiTypography
                              variant="body2"
                              color="white"
                              component="div"
                              dir={containsArabic(msg.text || "") ? "rtl" : "ltr"}
                              sx={{
                                fontSize: "14px",
                                lineHeight: 1.6,
                                wordBreak: "break-word",
                                whiteSpace: "pre-wrap", // Preserve line breaks (\n)
                                direction: containsArabic(msg.text || "") ? "rtl" : "ltr",
                                textAlign: containsArabic(msg.text || "") ? "right" : "left",
                                fontFamily: containsArabic(msg.text || "")
                                  ? '"Tajawal", "Cairo", "Roboto", sans-serif'
                                  : '"Roboto", sans-serif',
                                fontWeight: containsArabic(msg.text || "") ? 600 : 400,
                              }}
                            >
                              {msg.text || ""}
                            </VuiTypography>
                            <VuiBox
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 0.5,
                                mt: 1,
                              }}
                            >
                              {msg.time && (
                                <VuiTypography
                                  variant="caption"
                                  sx={{
                                    color: "rgba(255, 255, 255, 0.75)",
                                    fontSize: "11px",
                                  }}
                                >
                                  {msg.time}
                                </VuiTypography>
                              )}
                              {isAgent && (() => {
                                const status = messageStatuses[msg.id] || "sent";
                                if (status === "sending") {
                                  return (
                                    <MdSchedule
                                      size={14}
                                      color="rgba(255, 255, 255, 0.7)"
                                      style={{ animation: "pulse 1.5s ease-in-out infinite" }}
                                    />
                                  );
                                } else if (status === "sent") {
                                  return <MdDone size={14} color="rgba(255, 255, 255, 0.7)" />;
                                } else if (status === "failed") {
                                  return <MdError size={14} color="rgba(255, 0, 0, 0.9)" />;
                                }
                                return <MdDoneAll size={14} color="rgba(255, 255, 255, 0.9)" />;
                              })()}
                            </VuiBox>
                          </VuiBox>
                        </VuiBox>
                      );
                    })
                  ) : (
                    <VuiBox
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        color: text.main,
                      }}
                    >
                      <VuiTypography variant="body2" color="text">
                        No messages yet. Start the conversation!
                      </VuiTypography>
                    </VuiBox>
                  )}
                  <div ref={messagesEndRef} />
                </VuiBox>

                {/* Message Input */}
                <VuiBox
                  p={2.5}
                  sx={{
                    borderTop: `1px solid ${borderCol.navbar}`,
                    background: `linear-gradient(${card.deg}, ${card.main}, ${card.state})`,
                  }}
                >
                  <VuiBox display="flex" alignItems="flex-end" gap={1.5}>
                    <TextField
                      fullWidth
                      placeholder={isAIMode ? "AI is responding..." : "Type a message..."}
                      value={message}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setMessage(newValue);
                        setInputDirection(getTextDirection(newValue));
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={isAIMode}
                      multiline
                      maxRows={4}
                      dir={inputDirection}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: secondary.focus,
                          color: "white",
                          borderRadius: "24px",
                          direction: inputDirection,
                          "& fieldset": {
                            borderColor: borderCol.navbar,
                          },
                          "&:hover fieldset": {
                            borderColor: info.main,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: info.main,
                          },
                          "&.Mui-disabled": {
                            backgroundColor: secondary.main,
                            opacity: 0.6,
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "white",
                          fontSize: "15px",
                          direction: inputDirection,
                          textAlign: inputDirection === "rtl" ? "right" : "left",
                          fontFamily:
                            inputDirection === "rtl"
                              ? '"Tajawal", "Cairo", "Roboto", sans-serif'
                              : '"Roboto", sans-serif',
                          fontWeight: inputDirection === "rtl" ? 600 : 400,
                          padding: "12px 16px",
                          "&::placeholder": {
                            color: text.main,
                            opacity: 0.6,
                          },
                        },
                      }}
                    />
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={isAIMode || !message.trim()}
                      sx={{
                        backgroundColor: info.main,
                        color: "white",
                        width: "48px",
                        height: "48px",
                        "&:hover": {
                          backgroundColor: info.focus,
                          transform: "scale(1.05)",
                        },
                        "&:disabled": {
                          backgroundColor: secondary.focus,
                          color: text.main,
                          opacity: 0.5,
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <IoMdSend size={22} />
                    </IconButton>
                  </VuiBox>
                  {isAIMode && (
                    <VuiBox mt={1.5} display="flex" alignItems="center" gap={1}>
                      <BsRobot size={16} color={info.main} />
                      <VuiTypography variant="caption" color={text.main} sx={{ fontSize: "12px" }}>
                        AI is handling this conversation automatically
                      </VuiTypography>
                    </VuiBox>
                  )}
                </VuiBox>
              </Card>
            ) : (
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `linear-gradient(${card.deg}, ${card.main}, ${card.state})`,
                  borderRadius: "16px",
                  border: `1px solid ${borderCol.navbar}`,
                }}
              >
                <VuiBox display="flex" flexDirection="column" alignItems="center" p={3}>
                  <BsChatDots size={64} color={text.main} style={{ opacity: 0.3 }} />
                  <VuiTypography variant="h6" color={text.main} mt={2} textAlign="center">
                    Select a conversation to start chatting
                  </VuiTypography>
                </VuiBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ChatBate;

